/* Native Shopify suggestions. The search form remains usable without JS. */
(() => {
  if (customElements.get('kp-predictive')) return;
  const cache = new Map();
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.input = this.querySelector('input[name="q"]');
      this.panel = this.querySelector('.kp-predictive__panel');
      this.status = this.querySelector('[data-kp-predictive-status]');
      if (!this.input || !this.panel) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.input.addEventListener('input', () => {
        this.cancel();
        this.close();
        if (this.input.value.trim()) this.timer = setTimeout(() => this.search(), 220);
      }, options);
      this.input.addEventListener('focus', () => { if (this.input.value.trim()) this.search(); }, options);
      this.addEventListener('keydown', (event) => this.keydown(event), options);
      this.addEventListener('focusout', () => {
        setTimeout(() => { if (!this.contains(document.activeElement)) { this.cancel(); this.close(); } }, 0);
      }, options);
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) { this.cancel(); this.close(); }
      }, options);
      this.querySelector('form').addEventListener('submit', (event) => {
        if (!this.input.value.trim()) { event.preventDefault(); this.input.focus(); }
      }, options);
    }
    disconnectedCallback() { this.cancel(); this.events?.abort(); this.events = null; }
    cancel() { clearTimeout(this.timer); this.request?.abort(); this.sequence = (this.sequence || 0) + 1; }
    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.input.removeAttribute('aria-busy');
      this.active = -1;
    }
    async search() {
      const term = this.input.value.trim();
      if (!term) return;
      this.cancel();
      const sequence = this.sequence;
      this.request = new AbortController();
      const url = new URL(this.dataset.url, location.origin);
      url.search = new URLSearchParams({ q: term, section_id: 'kp-predictive-results',
        'resources[type]': 'query,collection,product', 'resources[limit]': '4',
        'resources[limit_scope]': 'each', 'resources[options][unavailable_products]': 'last' });
      this.input.setAttribute('aria-busy', 'true');
      this.status.textContent = this.panel.dataset.labelLoading;
      try {
        let html = cache.get(url.href);
        if (!html) {
          const response = await fetch(url, { signal: this.request.signal });
          if (!response.ok) throw new Error('Suggestions unavailable');
          html = await response.text();
          if (cache.size >= 20) cache.delete(cache.keys().next().value);
          cache.set(url.href, html);
        }
        if (sequence !== this.sequence || document.activeElement !== this.input) return;
        const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-kp-suggestions]');
        if (!content) throw new Error('Missing suggestions');
        this.panel.replaceChildren(content);
        this.items = [...this.panel.querySelectorAll('[role="option"]')];
        this.items.forEach((item, index) => { item.id = `${this.panel.id}-option-${index}`; });
        content.id = `${this.panel.id}-list`;
        this.input.setAttribute('aria-controls', content.id);
        this.active = -1;
        this.panel.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        this.status.textContent = content.dataset.status;
      } catch (error) {
        if (error.name !== 'AbortError' && sequence === this.sequence) {
          this.close();
          this.status.textContent = this.panel.dataset.labelError;
        }
      } finally {
        if (sequence === this.sequence) this.input.removeAttribute('aria-busy');
      }
    }
    keydown(event) {
      if (event.key === 'Escape') { this.cancel(); this.close(); return; }
      if (this.panel.hidden || !this.items?.length) return;
      if (event.key === 'Enter' && this.active >= 0) {
        event.preventDefault(); this.items[this.active].click(); return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      this.active = this.active < 0 ? (event.key === 'ArrowDown' ? 0 : this.items.length - 1)
        : (this.active + (event.key === 'ArrowDown' ? 1 : -1) + this.items.length) % this.items.length;
      this.items.forEach((item, index) => item.setAttribute('aria-selected', String(index === this.active)));
      this.input.setAttribute('aria-activedescendant', this.items[this.active].id);
      this.items[this.active].scrollIntoView({ block: 'nearest' });
    }
  }
  customElements.define('kp-predictive', PredictiveSearch);
})();
